import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { Chalet } from "./chalet.interface";
import { HMChalet, IDeviceState, ITarget } from "../data/chaletapi";
import mapInitState from "./mapInitState";


export type ChaletState = {
  data: Chalet;
  /** set when loading the initial state failed; cleared on success */
  loadError: string | null;
};

type ChaletStoreActions = {
  setData: (chalet: Chalet) => void;
  switchDevice: (switchId: string) => void;
  setLastUpdate: () => void;
  updateRoom: (roomId: string, action: string, value: any) => void;
  updateDevice: (
    roomId: string,
    deviceId: string,
    action: string,
    value: any
  ) => void;
  fetchInitialState: () => Promise<boolean>;
  switchLight: (lightId: string) => void;
  updateLight: (lightId: string, on: boolean) => void;
  resetInit: () => void;
  allOn: () => void;
  allOff: () => void;
};

type ChaletStore = ChaletState & ChaletStoreActions;
// pending retry of the initial fetch, cancelled on reset (logout)
let retryTimer: ReturnType<typeof setTimeout> | undefined
let retryDelay = 2000
const MAX_RETRY_DELAY = 60000

// sends a new setpoint to a room thermostat (BidCos-RF HM-CC-TC)
function sendSetpoint(address: string, value: number) {
  const statepayload: IDeviceState = {
    channel: address,
    datapoint: "SETPOINT",
    value: value,
    hminterface: "BidCos-RF",
  };
  HMChalet.postHMState(statepayload);
}

const initialstate = {
  loadError: null,
  data: {
    isInitilized: false, // this line causes issue for the VS Studio 
    rooms: {},
    devices: {},
    lights: {},
    lastUpdate: "",
    outside: -99,
    live: false,
  }
}
const chaletStore = create<ChaletStore>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialstate,
        setData: (data: Chalet) => set({ data }),
        fetchInitialState: async () => {
          clearTimeout(retryTimer)
          retryTimer = undefined
          try {
            const chaletState = await HMChalet.getChaletState();
            set({ data: mapInitState(chaletState), loadError: null });
            retryDelay = 2000
            return true
          } catch (e: any) {
            // keep isInitilized false so the dummy defaults are never shown as real values
            console.log('fetching initial state failed', e)
            set({ loadError: e?.message ?? 'unknown error' })
            retryTimer = setTimeout(() => { get().fetchInitialState() }, retryDelay)
            retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY)
            return false
          }
        },
        resetInit: () => {
          clearTimeout(retryTimer)
          retryTimer = undefined
          retryDelay = 2000
          set(initialstate)
        },
        switchLight: (lightId: string) => {
          const light = get().data.lights[lightId]
          if (!light) return
          const on = !light.switch
          set((state) => { state.data.lights[lightId].switch = on })
          // the indicator follows when the device reports the new state: via LIGHTevent on
          // the WebSocket, and by re-reading the state shortly after the command
          HMChalet.setLight(lightId, on).then(() => {
            setTimeout(() => {
              HMChalet.getChaletState()
                .then((s) => {
                  for (const l of s.lights ?? []) get().updateLight(l.id, l.on)
                })
                .catch((e: any) => console.log('reading light state failed', e))
            }, 1500)
          }).catch((e: any) => {
            console.log('switching light failed', e)
            set((state) => {
              const l = state.data.lights[lightId]
              if (l) l.switch = l.state
            })
          })
        },
        updateLight: (lightId: string, on: boolean) =>
          set((state) => {
            const light = state.data.lights[lightId]
            if (!light) {
              console.log('unknown light', lightId)
              return
            }
            light.state = on
            light.switch = on
          }),
        switchDevice: (devicdeId) =>
          set(
            (state) => {
              state.data.devices[devicdeId].state =
                !state.data.devices[devicdeId].state;
            },
            true,
            "switch"
          ),
        updateRoom: (roomId: string, action: string, value: any) =>
          set(
            (state) => {
              if (!Object.hasOwn(state.data.rooms, roomId)) {
                console.log('unknown room', roomId)
                return
              }

              if (value == "none") {
                return;
              }
              switch (action) {
                case "updateActual": {
                  state.data.rooms[roomId].actualTemp = value;
                  break;
                }
                case "updateTarget": {
                  state.data.rooms[roomId].targetTemp = value;
                  break;
                }
                case "updateTargetOn": {
                  state.data.rooms[roomId].targetTempOn = value;
                  const targetPayload : ITarget= {
                    room: roomId,
                    value: value,
                    target: 'TARGETON'
                  }
                  HMChalet.writeTarget(targetPayload);
                  // room is on: the thermostat must follow the new on target
                  if (state.data.rooms[roomId].switch_state) {
                    state.data.rooms[roomId].targetTemp = value
                    sendSetpoint(state.data.rooms[roomId].addressSetPoint, value)
                  }

                  break;
                }
                case "updateTargetOff": {
                  state.data.rooms[roomId].targetTempOff = value;
                  const targetPayload : ITarget= {
                    room: roomId,
                    value: value,
                    target: 'TARGETOFF'
                  }
                  HMChalet.writeTarget(targetPayload);
                  // room is off: the thermostat must follow the new off target
                  if (!state.data.rooms[roomId].switch_state) {
                    state.data.rooms[roomId].targetTemp = value
                    sendSetpoint(state.data.rooms[roomId].addressSetPoint, value)
                  }

                  break;
                }

                case "switchHeater": {
                  if (value == "1") {
                    state.data.rooms[roomId].thermState = "On";
                  } else {
                    state.data.rooms[roomId].thermState = "Off";
                  }
                  break;
                }
                case "setOnOff": {
                  var newTarget: number = state.data.rooms[roomId].targetTemp;
                  if (state.data.rooms[roomId].switch_state) {
                    // on -> off
                    state.data.rooms[roomId].switch_state = false;
                    newTarget = state.data.rooms[roomId].targetTempOff;
                  } else if (!state.data.rooms[roomId].switch_state) {
                    // off -> on
                    state.data.rooms[roomId].switch_state = true;
                    newTarget = state.data.rooms[roomId].targetTempOn;
                  }
                  state.data.rooms[roomId].targetTemp = newTarget;
                  const statepayload: IDeviceState = {
                    channel: state.data.rooms[roomId].addressSetPoint,
                    datapoint: "SETPOINT",
                    value: newTarget,
                    hminterface: "BidCos-RF",
                  };
                  HMChalet.postHMState(statepayload);
                }
              }
            },
            true,
            "updateRoom"
          ),
        updateDevice: (
          roomId: string,
          deviceId: string,
          action: string,
          value: any
        ) =>
          set((state) => {

            if (!Object.hasOwn(state.data.devices, deviceId)) {
              console.log('unknown device', deviceId)
              return
            }

            if (value == "none") {
              return;
            }
            console.log(roomId, deviceId, action, value);
            switch (action) {
              case "updateStatus": {
                // values arrive as strings, so !!"0" would be true
                state.data.devices[deviceId].switch = value === true || Number(value) === 1;
                break;
              }
              case 'onOff': {
                state.data.devices[deviceId].switch = !state.data.devices[deviceId].switch
                var newvalue = 0
                if (state.data.devices[deviceId].switch) {
                  newvalue = 1
                }
                const statepayload: IDeviceState = {
                  channel: state.data.devices[deviceId].address,
                  datapoint: 'STATE',
                  value: newvalue,
                  hminterface: 'HmIP-RF'
                }
                HMChalet.postHMState(statepayload)
                break;
              }
            }
          }),
        setLastUpdate: () =>
          set((state) => {
            state.data.lastUpdate = new Date(Date.now()).toString();
          }),
        allOn: () =>
          set((state) => {
            for (const roomkey in state.data.rooms) {
              const room = state.data.rooms[roomkey]
              room.targetTemp = room.targetTempOn
              room.switch_state = true
              const statepayload: IDeviceState = {
                channel: room.addressSetPoint,
                datapoint: "SETPOINT",
                value: room.targetTemp,
                hminterface: "BidCos-RF",
              }
              HMChalet.postHMState(statepayload)
            }
            for (const devicekey in state.data.devices) {
              const device = state.data.devices[devicekey]
              // type 2 (fire place) has no switch of its own
              if (device.deviceType === 2) continue
              device.switch = true
              const statepayload: IDeviceState = {
                channel: device.address,
                datapoint: 'STATE',
                value: 1,
                hminterface: 'HmIP-RF'
              }
              HMChalet.postHMState(statepayload)
            }
          }),
        allOff: () =>  // switch all off by iterating over the rooms and devices and sening a set point and state 
          set((state) => {
            for (const roomkey in state.data.rooms) {
              const room = state.data.rooms[roomkey]
              room.targetTemp = room.targetTempOff
              room.switch_state = false
              const statepayload: IDeviceState = {
                channel: room.addressSetPoint,
                datapoint: "SETPOINT",
                value: room.targetTemp,
                hminterface: "BidCos-RF",
              }
              HMChalet.postHMState(statepayload)
            };
            for (const devicekey in state.data.devices) {
              const device = state.data.devices[devicekey]
              // type 2 (fire place) has no switch of its own
              if (device.deviceType === 2) continue
              device.switch = false
              const statepayload: IDeviceState = {
                channel: device.address,
                datapoint: 'STATE',
                value: 0,
                hminterface: 'HmIP-RF'
              }
              HMChalet.postHMState(statepayload)
            }
          }), enabled: true, name: "chaletStore"
      })
    )
  )
);

export default chaletStore;
